/**
 * One member's usage — what a single person actually did with LedAI.
 *
 * ## The question this answers, and where it came from
 *
 * The Brief's "Who is using LedAI" panel and the People roster both end in a
 * row that says a number and goes nowhere useful. `?person=` has been on those
 * links since the adoption panel shipped; until now the People page ignored it
 * and drew the whole roster again. This is the page that parameter was always
 * pointing at.
 *
 * ## It MIRRORS the admin dashboard; it does not proxy it
 *
 * `backend` answers the same question internally —
 * `GET /dashboard-metrics/user/:userId`, drawn by
 * `apps/pulse`. Its sections are the sections here, in its
 * order and its vocabulary, so the two read as one product: totals, activity
 * over time, record coverage by kind, their walks.
 *
 * What is NOT here is as deliberate. That endpoint is mounted at the Express
 * ROOT outside Clerk, authenticates with a shared internal token, and reads a
 * whole environment database; forwarding a console request to it would hand
 * this reader numbers from projects they cannot open. So every figure below is
 * computed by `server/queries/getMemberUsage.ts` from what the console already
 * reads, bounded by the caller's job-site ceiling — and the dashboard's
 * remaining sections are OMITTED rather than invented:
 *
 *  - device / phone-app / settings (app version, firmware, glasses display
 *    mode, capture sensitivity, review stage, report fields) — per-user
 *    documents the console does not read and has no ceiling to bound;
 *  - Procore connection state — the member's own integration credentials;
 *  - `withAssignee` in coverage — `ConsoleItem` carries no assignee, and the
 *    nearest field is `ballHolder`, which this console does not render at all;
 *  - the walk heatmap and timezone-aware days — the console holds no timezone
 *    for this member, and bucketing by one nobody chose moves walks across
 *    day boundaries invisibly.
 *
 * ## Zero is a finding, not an absence
 *
 * Every section renders at zero. A seat that has never walked is the single
 * most valuable row the adoption panel produces, and opening it must give a
 * page that says so — not an error, and not a blank.
 */

import {
  fetchMemberUsage,
  type MemberUsageCoverageRow,
  type MemberUsageResponse,
  type MemberUsageTrendRow,
  type MemberUsageWalkRow,
} from "../api";
import { useResource } from "../useResource";
import { C, FONT_MONO, projectDot } from "../tokens";
import type { ConsoleItemKind } from "../../server/projection/consoleItem";
import {
  Card,
  ConsoleViewProps,
  EmptyPanel,
  Eyebrow,
  FailurePanel,
  KIND_LABEL_PLURAL,
  LoadingPanel,
  META,
  StaleBanner,
  ViewBody,
  ViewHeader,
  plural,
  whenStamp,
} from "../components/chrome";

/**
 * The coverage block's labels.
 *
 * PLURAL, because this block counts a SET: "Daily log 4" reads as a name
 * where "Daily logs 4" reads as a count. The table used to live here; it is
 * now `chrome`'s, because the Brief's by-kind rows count a set too and a
 * second copy is a second thing to drift.
 */
const COVERAGE_LABEL = KIND_LABEL_PLURAL;

/** Recorded time, in the units the adoption Brief already speaks. */
function hoursLabel(ms: number): string {
  return `${(ms / 3_600_000).toFixed(1)}h`;
}

/** `2026-08-26` → `26 AUG`, for the trend's axis. */
function dayTick(date: string): string {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed
    .toLocaleDateString("en-US", { day: "numeric", month: "short", timeZone: "UTC" })
    .toUpperCase();
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function StatTile({ value, label, note }: { value: string; label: string; note: string }) {
  return (
    <Card style={{ padding: "14px 16px" }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ ...META, fontSize: 10.35, letterSpacing: "0.9px", color: C.sub, marginTop: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 13.8, color: C.sub2, marginTop: 4 }}>{note}</div>
    </Card>
  );
}

/**
 * Activity over time — one column per day, zeros included.
 *
 * Bars are scaled against the busiest day in the window rather than against a
 * fixed ceiling, so a quiet week is still readable. A day with nothing on it
 * keeps its column and its date: a gap the reader has to interpret is worse
 * than a zero that states itself, which is why the server scaffolds every day.
 */
function TrendStrip({ rows }: { rows: MemberUsageTrendRow[] }) {
  const peak = Math.max(1, ...rows.map((row) => row.walks));

  return (
    <div
      data-testid="member-usage-trend"
      style={{ display: "flex", alignItems: "flex-end", gap: 4, minHeight: 96 }}
    >
      {rows.map((row) => {
        const height = Math.round((row.walks / peak) * 64);
        return (
          <div
            key={row.date}
            data-testid="member-usage-day"
            data-date={row.date}
            data-walks={row.walks}
            title={`${row.date} · ${plural(row.walks, "walk", "walks")} · ${
              row.recordedMinutes
            } min · ${plural(row.records, "record", "records")}`}
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              aria-hidden
              style={{
                width: "100%",
                // A day with no walks keeps a visible sliver on the empty
                // track, so the column reads as measured-and-zero rather than
                // as a hole in the chart.
                height: Math.max(2, height),
                background: row.walks > 0 ? C.ink : C.barEmpty,
                borderRadius: 3,
              }}
            />
            <span
              style={{
                ...META,
                fontSize: 9.2,
                letterSpacing: "0.3px",
                color: C.sub2,
                whiteSpace: "nowrap",
              }}
            >
              {dayTick(row.date)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** One kind's coverage: how many, and how many name where. */
function CoverageRow({ row }: { row: MemberUsageCoverageRow }) {
  const percent = row.total ? Math.round((row.withLocation / row.total) * 100) : 0;
  return (
    <div
      data-testid="member-usage-kind"
      data-kind={row.kind}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <span style={{ flex: 1, fontSize: 14.95, fontWeight: 600 }}>{COVERAGE_LABEL[row.kind]}</span>
      <span style={{ width: 90, height: 6, background: C.track, borderRadius: 3, flexShrink: 0 }}>
        <span
          style={{
            display: "block",
            width: `${percent}%`,
            height: "100%",
            background: row.total ? C.green : C.barEmpty,
            borderRadius: 3,
          }}
        />
      </span>
      <span
        style={{
          width: 116,
          flexShrink: 0,
          textAlign: "right",
          fontFamily: FONT_MONO,
          fontSize: 12.65,
          color: row.total ? C.sub : C.sub2,
        }}
      >
        {row.total} · {row.withLocation} located
      </span>
    </div>
  );
}

/** One walk. Length is visit metadata, never audio content (spec §7.3). */
function WalkRow({
  walk,
  href,
  onOpen,
}: {
  walk: MemberUsageWalkRow;
  href: string;
  onOpen: () => void;
}) {
  return (
    <a
      className="kb"
      data-testid="member-usage-walk"
      data-walk-id={walk.id}
      href={href}
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 0",
        borderBottom: `1px solid ${C.line}`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: 4,
          background: projectDot(walk.jobSiteId),
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 15.52,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {/* The server sends `""` for an unnamed walk rather than inventing a
              name. The placeholder is a DISPLAY choice and belongs here. */}
          {walk.name || "Untitled walk"}
        </span>
        <span style={{ display: "block", fontSize: 13.8, color: C.sub2, marginTop: 3 }}>
          {walk.siteName} · {whenStamp(walk.startedAt)}
        </span>
      </span>
      <span
        style={{
          width: 74,
          flexShrink: 0,
          textAlign: "right",
          fontFamily: FONT_MONO,
          fontSize: 12.65,
          color: C.sub,
        }}
      >
        {/* `undefined` minutes means UNKNOWN, not zero — a walk still open with
            no closed span has no length yet, and `0 MIN` would assert it
            recorded nothing. */}
        {walk.recordedMinutes === undefined ? "—" : `${walk.recordedMinutes} MIN`}
      </span>
      <span
        style={{
          width: 78,
          flexShrink: 0,
          textAlign: "right",
          fontFamily: FONT_MONO,
          fontSize: 12.65,
          color: walk.itemCount ? C.ink : C.sub2,
        }}
      >
        {plural(walk.itemCount, "record", "records")}
      </span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export interface MemberUsageProps extends ConsoleViewProps {
  /** Whose usage — the `?person=` on the URL. */
  personId: string;
  /** Back to the roster: the same URL with `?person=` removed. */
  backHref: string;
  onBack: () => void;
}

export function MemberUsage({
  personId,
  backHref,
  onBack,
  search,
  refreshKey,
  personalHref,
  onGoPersonal,
  hrefForWalk,
  onOpenWalk,
}: MemberUsageProps) {
  const { data, failure, loading, refreshFailure } = useResource<MemberUsageResponse>(
    `member-usage:${personId}:${search}`,
    () => fetchMemberUsage(personId, search),
    refreshKey,
  );

  if (failure) {
    return (
      <FailurePanel failure={failure} personalHref={personalHref} onGoPersonal={onGoPersonal} />
    );
  }
  if (!data) {
    return loading ? (
      <LoadingPanel shape="member" />
    ) : (
      <EmptyPanel title="This person" body="Nothing loaded." />
    );
  }

  const back = (
    <a
      className="kb"
      data-testid="member-usage-back"
      href={backHref}
      onClick={(event) => {
        event.preventDefault();
        onBack();
      }}
      style={{ ...META, fontSize: 10.93, letterSpacing: "0.9px", color: C.sub }}
    >
      ← All people
    </a>
  );

  // Nobody to report on. A page of zeros for someone who is not a member would
  // read as "this person does nothing", which is a claim about somebody who is
  // not here — so this says WHICH silence it is instead.
  if (!data.member) {
    return (
      <>
        <ViewHeader crumb="People" title="This person" />
        <ViewBody>
          {back}
          <div data-testid="member-usage-unavailable" style={{ marginTop: 16 }}>
            <EmptyPanel
              title={data.hasOrg ? "No seat to report on" : "No organization"}
              body={
                data.unavailableReason ??
                "There is nobody to report on for this link."
              }
            />
          </div>
        </ViewBody>
      </>
    );
  }

  const member = data.member;
  const windowLabel =
    data.withinDays === 7 ? "THIS WEEK" : `LAST ${Math.round(data.withinDays)} DAYS`;

  return (
    <>
      <ViewHeader
        crumb={`People · ${windowLabel}`}
        title={member.name}
        // The email, when Clerk has one — display names collide in the real
        // org ("Leo Li" twice), and the email is what people identify an
        // account by. The role is the fallback, not the other way round.
        note={member.email ?? member.role.toUpperCase()}
      />

      <ViewBody>
        {refreshFailure && <StaleBanner failure={refreshFailure} since={data.generatedAt} />}

        <div data-testid="member-usage" style={{ marginBottom: 8 }}>
          {back}

          {/* ---------- totals ---------- */}
          <div
            data-testid="member-usage-totals"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              margin: "14px 0 26px",
            }}
          >
            <StatTile
              value={String(data.totals.walks)}
              label="walks captured"
              note={
                member.lastSignInAt === null
                  ? "This seat has never been signed into"
                  : `on projects you can see · ${windowLabel.toLowerCase()}`
              }
            />
            <StatTile
              value={hoursLabel(data.totals.recordedMs)}
              label="field time recorded"
              note={
                data.totals.walks
                  ? `avg ${Math.round(
                      data.totals.recordedMinutes / data.totals.walks,
                    )} min per walk`
                  : "no walks yet"
              }
            />
            <StatTile
              value={String(data.totals.records)}
              label="records created"
              note="none typed by hand"
            />
          </div>

          {/* ---------- activity over time ---------- */}
          <div style={{ marginBottom: 26 }}>
            <Eyebrow
              right={
                <span style={{ ...META, fontSize: 10.35, color: C.sub2 }}>
                  {/* Days are UTC, and the page says so rather than implying a
                      local zone the console does not have for this member. */}
                  {`${plural(data.trend.length, "day", "days")} · ${data.trendTimeZone}`}
                </span>
              }
            >
              Activity over time
            </Eyebrow>
            <Card style={{ padding: "16px 16px 10px" }}>
              <TrendStrip rows={data.trend} />
            </Card>
          </div>

          {/* ---------- record coverage ---------- */}
          <div style={{ marginBottom: 26 }}>
            <Eyebrow>What they captured</Eyebrow>
            <Card testId="member-usage-coverage" style={{ padding: "4px 16px 6px" }}>
              {data.recordCoverage.map((row) => (
                <CoverageRow key={row.kind} row={row} />
              ))}
            </Card>
          </div>

          {/* ---------- their walks ---------- */}
          <div style={{ marginBottom: 22 }}>
            <Eyebrow
              right={
                data.walks.truncated ? (
                  <span style={{ ...META, fontSize: 10.35, color: C.sub2 }}>
                    {`${plural(data.walks.total, "walk", "walks")} in all, newest listed`}
                  </span>
                ) : undefined
              }
            >
              Their walks
            </Eyebrow>
            <Card style={{ padding: "4px 16px 6px" }}>
              {data.walks.items.length === 0 ? (
                <div
                  data-testid="member-usage-no-walks"
                  style={{ padding: "14px 0", fontSize: 14.37, color: C.sub, lineHeight: 1.5 }}
                >
                  {member.name} has not walked a project you can see in this range.
                  {member.lastSignInAt === null
                    ? " This seat has never been signed into."
                    : ""}
                </div>
              ) : (
                data.walks.items.map((walk) => (
                  <WalkRow
                    key={walk.id}
                    walk={walk}
                    href={hrefForWalk(walk.id)}
                    onOpen={() => onOpenWalk(walk.id)}
                  />
                ))
              )}
            </Card>
          </div>

          <span data-testid="member-usage-footer" style={{ fontSize: 13.8, color: C.sub2, lineHeight: 1.5 }}>
            A lookup, not a leaderboard. These numbers count only the projects you can see, so
            they are this person's work in your scope rather than their whole week.
          </span>
        </div>
      </ViewBody>
    </>
  );
}
